import { useState } from 'react';
import { Alert, Badge, Button, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCreditCard, IconEdit, IconPlus } from '@tabler/icons-react';
import { CountryCodes } from '@lib2/model/form/CountryCodes';
import { useCardOnFile, usePaymentMethod } from '../../hooks/useBilling';
import { CardEntryUtils } from '../../CardEntryUtils';
import { StripeCardForm } from './StripeCardForm';
import type { ICardSummary } from '@/lib/types/Billing';

/**
 * Billing → Payment Method with FEATURE_FLAGS.billingCardEntryEnabled on: the card on
 * file comes from the billing profile (GET billing/card); name and address still come
 * from the billing account (notes/billing-card-entry.md D7). Card entry = StripeCardForm.
 */
export function StripePaymentMethodTab() {
  const { paymentMethod, isLoading: isAddressLoading, isError: isAddressError } = usePaymentMethod();
  const cardOnFile = useCardOnFile();
  const [isEditing, setIsEditing] = useState(false);

  if (isAddressLoading || cardOnFile.isLoading) {
    return <Skeleton height={200} radius="md" />;
  }

  if (cardOnFile.isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />} title="Payment method unavailable">
        <Stack gap="xs" align="flex-start">
          <Text size="sm">Failed to load your card on file. Please try again.</Text>
          <Button size="xs" variant="light" onClick={() => cardOnFile.refetch()}>
            Try again
          </Button>
        </Stack>
      </Alert>
    );
  }

  function handleSaved(card: ICardSummary) {
    setIsEditing(false);
    notifications.show({
      title: 'Saved',
      message: `${CardEntryUtils.cardSummaryLabel(card)} is now your card on file.`,
      color: 'green',
    });
  }

  if (isEditing) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text fw={600} size="lg" mb="md">{cardOnFile.card ? 'Edit Payment Method' : 'Add Payment Method'}</Text>
        <StripeCardForm
          defaults={CardEntryUtils.billingDefaults(paymentMethod)}
          onSaved={handleSaved}
          onCancel={() => setIsEditing(false)}
        />
      </Card>
    );
  }

  const card = cardOnFile.card;
  const now = new Date();
  const isExpired = card ? CardEntryUtils.isExpired(card, { year: now.getFullYear(), month: now.getMonth() + 1 }) : false;
  const hasAddress = !!paymentMethod?.fullName;
  const countryLabel = paymentMethod
    ? CountryCodes.getLabel(paymentMethod.countryIso2) ?? paymentMethod.countryIso2
    : '';

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text fw={600} size="lg">Payment Method</Text>
        <Button
          leftSection={card ? <IconEdit size={16} /> : <IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          {card ? 'Edit Payment Method' : 'Add Payment Method'}
        </Button>
      </Group>

      <Stack gap="sm">
        {isAddressError && (
          <Text size="sm" c="dimmed">Billing address is unavailable right now.</Text>
        )}
        {hasAddress && paymentMethod && (
          <>
            {paymentMethod.orgName && <InfoRow label="Organization" value={paymentMethod.orgName} />}
            <InfoRow label="Name" value={paymentMethod.fullName} />
            <InfoRow
              label="Address"
              value={[
                paymentMethod.address1,
                `${paymentMethod.city}, ${paymentMethod.region} ${paymentMethod.postalCode}`,
                countryLabel,
              ].join('\n')}
              multiline
            />
          </>
        )}
        {card ? (
          <InfoRow
            label="Card on File"
            value={`${CardEntryUtils.cardSummaryLabel(card)} · Expires ${CardEntryUtils.formatExpiry(card.expMonth, card.expYear)}`}
            icon={<IconCreditCard size={16} />}
            badge={isExpired ? <Badge color="red" variant="light" size="sm">Expired</Badge> : undefined}
          />
        ) : (
          <InfoRow label="Card on File" value="No card on file" icon={<IconCreditCard size={16} />} />
        )}
      </Stack>
    </Card>
  );
}

function InfoRow({
  label,
  value,
  icon,
  badge,
  multiline,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  multiline?: boolean;
}) {
  return (
    <Group gap="md" align="flex-start">
      <Text size="sm" c="dimmed" w={120} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Group gap="xs">
        {icon}
        <Text size="sm" fw={500} style={multiline ? { whiteSpace: 'pre-line' } : undefined}>
          {value}
        </Text>
        {badge}
      </Group>
    </Group>
  );
}
